import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from '../../shared/Modal'
import { showToast } from '../../shared/Toast'
import IssuesService from '../../../services/api/IssuesService'
import CloseIcon from '../../ui/icons/CloseIcon'
import StatusSelect from './StatusSelect'
import { TOAST_CONTAINER_ID } from './constants'
import { pluralize } from '../../../utils/textHelpers'

const TITLE = 'Changer le statut'
const ERROR_MSG = 'Une erreur est survenue lors de la modification des alertes.'
const CONTENT_CLASS = 'tw:rounded! tw:border-0! tw:overflow-hidden '
  + 'tw:shadow-[0_0_1px_0_rgba(30,31,33,0.31),0_8px_12px_0_rgba(30,31,33,0.15)]!'
const BODY_CLASS = 'tw:px-4! tw:py-0!'
const FIELD_CLASS = 'tw:flex tw:flex-col tw:gap-1'
const LABEL_CLASS = 'tw:mb-0! tw:text-xs tw:font-medium tw:text-gray-900'
const FOOTER_CLASS = 'tw:p-4! tw:border-0!'
const BACKDROP_CLASS = 'tw:bg-[#444]! tw:[&.show]:opacity-40!'
const HEADER_CLASS = 'tw:flex tw:items-start tw:gap-4 tw:p-4 tw:bg-white'
const TITLE_WRAP_CLASS = 'tw:flex tw:flex-1 tw:items-center tw:min-h-8 tw:min-w-0'
const TITLE_CLASS = 'tw:m-0! tw:text-base! tw:font-medium! tw:leading-6! tw:text-[#292a2e]!'
const CLOSE_BTN_CLASS = 'tw:flex tw:items-center tw:justify-center tw:p-1 tw:rounded-md! '
  + 'tw:bg-white tw:border-0 tw:cursor-pointer tw:text-[#4b5563] tw:hover:bg-gray-100'
const FOOTER_ACTIONS_CLASS = 'tw:flex tw:items-center tw:justify-end tw:gap-2 tw:w-full tw:m-0!'
const ACTION_BTN_CLASS = 'tw:inline-flex tw:items-center tw:justify-center tw:min-h-9 tw:min-w-9 '
  + 'tw:px-3 tw:py-2 tw:rounded! tw:border-0 tw:text-sm tw:font-medium tw:leading-5! tw:cursor-pointer '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'
const CANCEL_BTN_CLASS = `${ACTION_BTN_CLASS} tw:bg-transparent tw:text-[#374151] tw:hover:bg-gray-100`
const SAVE_BTN_CLASS = `${ACTION_BTN_CLASS} tw:bg-[#334155] tw:text-white tw:hover:bg-[#1e293b]`

const successMessage = (count, statusName) => {
  if (count === 0)
    return 'Aucune alerte modifiée'
  const verb = count > 1 ? 'passées' : 'passée'
  return `${pluralize(count, 'alerte')} ${verb} à l’étape ${statusName}`
}

const toastVariant = count => count === 0 ? 'info' : 'success'

const BatchStatusModal = ({ collectId, isOpen, issueStatuses, scope, onClose, onUpdated }) => {
  const [statusId, setStatusId] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setStatusId(null)
      setSaving(false)
    }
  }, [isOpen])

  const handleSave = useCallback(async() => {
    if (!statusId || saving)
      return
    setSaving(true)
    try {
      const { count } = await IssuesService.batchUpdate(collectId, { ...scope, issueStatusId: statusId })
      const issueStatus = issueStatuses.find(status => status.id === statusId)
      showToast(successMessage(count, issueStatus?.name), toastVariant(count), TOAST_CONTAINER_ID)
      onUpdated()
      onClose()
    } catch {
      showToast(ERROR_MSG, 'error', TOAST_CONTAINER_ID)
    } finally {
      setSaving(false)
    }
  }, [collectId, scope, statusId, saving, issueStatuses, onUpdated, onClose])

  const handleClose = useCallback(() => {
    if (saving)
      return
    onClose()
  }, [saving, onClose])

  const header = (
    <div className={HEADER_CLASS}>
      <div className={TITLE_WRAP_CLASS}>
        <h5 className={TITLE_CLASS}>{TITLE}</h5>
      </div>
      <button aria-label="Fermer la modale" className={CLOSE_BTN_CLASS} type="button" onClick={handleClose}>
        <CloseIcon className="tw:size-6" />
      </button>
    </div>
  )

  const footer = (
    <div className={FOOTER_ACTIONS_CLASS}>
      <button className={CANCEL_BTN_CLASS} disabled={saving} type="button" onClick={handleClose}>
        Annuler
      </button>
      <button className={SAVE_BTN_CLASS} disabled={!statusId || saving} type="button" onClick={handleSave}>
        Enregistrer
      </button>
    </div>
  )

  return (
    <Modal
      backdropClassName={BACKDROP_CLASS}
      bodyClassName={BODY_CLASS}
      contentClassName={CONTENT_CLASS}
      footer={footer}
      footerClassName={FOOTER_CLASS}
      header={header}
      isOpen={isOpen}
      size="compact"
      title={TITLE}
      onClose={handleClose}
    >
      <div className={FIELD_CLASS}>
        <label className={LABEL_CLASS} htmlFor="batch-issue-status">Nouveau statut</label>
        <StatusSelect
          inputId="batch-issue-status"
          issueStatuses={issueStatuses}
          placeholder="Sélectionner"
          value={statusId}
          onChange={setStatusId}
        />
      </div>
    </Modal>
  )
}

BatchStatusModal.propTypes = {
  collectId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  issueStatuses: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    allFiltered: PropTypes.bool,
    issueIds: PropTypes.arrayOf(PropTypes.number)
  }).isRequired
}

BatchStatusModal.defaultProps = {
  issueStatuses: []
}

export default BatchStatusModal
